package com.eladfinish.bible.contest.features.review

import com.eladfinish.bible.contest.features.installTime.domain.repository.InstallInfoRepository
import kotlin.time.Duration.Companion.days
import kotlin.time.Duration.Companion.hours

enum class ReviewEligibility {
    ELIGIBLE,
    DISABLED,
    NO_INSTALL_INFO,
    INSTALLED_TOO_RECENTLY,
    UPDATED_TOO_RECENTLY,
    NOT_ENOUGH_SUCCESSES,
    NOT_ENOUGH_ACTIVE_DAYS,
    COOLDOWN,
}

/**
 * Whether this user, right now, is someone to ask for a review.
 *
 * The stores forbid asking the user anything before their sheet (Google) and disallow custom
 * review prompts (Apple), so there is no "do you like the app?" gate. What keeps the request
 * away from an unhappy user is this: it goes only to someone who kept the app for days, came
 * back, and succeeded in it several times, and not right after an update.
 */
class ShouldRequestReviewUseCase(
    private val progress: ReviewProgress,
    private val installInfoRepository: InstallInfoRepository,
    private val nowMillis: () -> Long,
) {
    operator fun invoke(config: InAppReviewConfig): ReviewEligibility {
        if (!config.enabled) return ReviewEligibility.DISABLED

        val installInfo = installInfoRepository.getInstallInfo() ?: return ReviewEligibility.NO_INSTALL_INFO
        val now = nowMillis()
        val lastRequest = progress.lastRequestMillis

        return when {
            now - installInfo.firstInstallTimeMillis < config.minDaysSinceInstall.days.inWholeMilliseconds ->
                ReviewEligibility.INSTALLED_TOO_RECENTLY

            now - installInfo.lastUpdateTimeMillis < config.minHoursSinceUpdate.hours.inWholeMilliseconds ->
                ReviewEligibility.UPDATED_TOO_RECENTLY

            progress.successCount < config.minSuccesses -> ReviewEligibility.NOT_ENOUGH_SUCCESSES

            progress.activeDays < config.minActiveDays -> ReviewEligibility.NOT_ENOUGH_ACTIVE_DAYS

            lastRequest != 0L && now - lastRequest < config.cooldownDays.days.inWholeMilliseconds ->
                ReviewEligibility.COOLDOWN

            else -> ReviewEligibility.ELIGIBLE
        }
    }

    fun daysSinceInstall(): Long? {
        val installInfo = installInfoRepository.getInstallInfo() ?: return null
        return (nowMillis() - installInfo.firstInstallTimeMillis) / 1.days.inWholeMilliseconds
    }
}
